import { useReceptionLocale as useOperationsLocale } from './locale';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RealtimeContext, type ReceptionEvent, type RealtimeContextShape } from './RealtimeContext';
import { useToast } from '../ui/hooks';
import { useAuth } from '../auth/useAuth';
import { dispatchLogoutAfterAuthClear } from '../auth/logoutBoundary';
import { useWebSocket } from '../hooks/useWebSocket';
import { fetchReceptionEventsSince, fetchReceptionWsToken } from '../api/realtime';
import { getAccountantInvalidationsForEvent, getReceptionInvalidationsForEvent } from '../queries/realtime/invalidation';
import { resolveEmployeeLoginPathFromUser } from '../pages/employee/employeeAuthPaths';
import { clearRealtimeInvalidations, scheduleRealtimeInvalidation } from '../queries/realtime/scheduleInvalidation';

const MAX_RETRY = 8;
const EVENT_DEDUPE_TTL_MS = 30_000;
const MAX_EVENT_DEDUPE_SIZE = 400;
const APPROVAL_TOAST_DEDUPE_MS = 10_000;
const BACKFILL_POLL_INTERVAL_MS = 4_000;

interface RealtimeProviderProps { children: React.ReactNode; enabled?: boolean }

interface RawEvent {
  event?: string;
  event_type?: string;
  event_id?: string;
  entity_id?: number | string | null;
  record_id?: number | string | null;
  id?: number | string | null;
  source?: string;
  origin?: string;
  action?: string;
  table?: string;
  toast?: {
    message?: string;
    type?: 'success' | 'info' | 'warning' | 'danger';
    durationMs?: number;
  } | null;
  [k: string]: unknown;
}

function normalizeIncomingEvent(parsed: unknown): ReceptionEvent | null {
  if (!parsed || typeof parsed !== 'object') return null;
  const raw = parsed as RawEvent;
  if (raw.event === 'history' && raw.event_type) {
    const normalized: ReceptionEvent = {
      ...(raw as Record<string, unknown>),
      event: String(raw.event_type),
    };
    delete (normalized as Record<string, unknown>).event_type;
    return normalized;
  }
  return raw as ReceptionEvent;
}

function deriveEventType(payload: ReceptionEvent): string {
  if (typeof payload.event === 'string' && payload.event.trim()) return payload.event.trim();
  if (typeof payload.event_type === 'string' && payload.event_type.trim()) return payload.event_type.trim();
  return '';
}

function deriveChangeSignature(payload: ReceptionEvent): string {
  const directChanges = (payload as { changes?: unknown }).changes;
  if (directChanges && typeof directChanges === 'object') {
    try {
      return JSON.stringify(directChanges);
    } catch {
      return '';
    }
  }
  const rawPayload = (payload as { payload?: unknown }).payload;
  if (rawPayload && typeof rawPayload === 'object') {
    const payloadObj = rawPayload as Record<string, unknown>;
    const nestedChanges = payloadObj.changes;
    if (nestedChanges && typeof nestedChanges === 'object') {
      try {
        return JSON.stringify(nestedChanges);
      } catch {
        return '';
      }
    }
    const compact: Record<string, unknown> = {};
    for (const key of ['branch', 'intended_course', 'placement_completed_at', 'progress_status', 'status']) {
      if (Object.prototype.hasOwnProperty.call(payloadObj, key)) {
        compact[key] = payloadObj[key];
      }
    }
    if (Object.keys(compact).length > 0) {
      try {
        return JSON.stringify(compact);
      } catch {
        return '';
      }
    }
  }
  return '';
}

function deriveEventId(payload: ReceptionEvent, eventType: string): string {
  if (typeof payload.event_id === 'string' && payload.event_id.trim()) return payload.event_id;
  const idCandidate =
    payload.entity_id ??
    payload.record_id ??
    payload.id ??
    payload.student_id ??
    payload.group_id ??
    payload.invoice_id ??
    payload.task_id ??
    payload.request_id ??
    payload.payment_id;
  const occurredAt = (payload.occurred_at ?? payload.timestamp ?? '') as string | number;
  const changesSig = deriveChangeSignature(payload);
  if (!idCandidate && !occurredAt && !changesSig) {
    // Weak identity: skip dedupe to avoid dropping legitimate updates.
    return '';
  }
  return `${eventType}:${String(idCandidate ?? '')}:${String(occurredAt)}:${changesSig}`;
}

function pruneDedupeMap(map: Map<string, number>, nowMs: number): void {
  for (const [key, ts] of map.entries()) {
    if (nowMs - ts > EVENT_DEDUPE_TTL_MS) {
      map.delete(key);
    }
  }
  if (map.size > MAX_EVENT_DEDUPE_SIZE) {
    const ordered = [...map.entries()].sort((a, b) => a[1] - b[1]);
    for (const [key] of ordered.slice(0, map.size - MAX_EVENT_DEDUPE_SIZE)) {
      map.delete(key);
    }
  }
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children, enabled = true }) => {
  const { format: operationsFormat, t: operationsText } = useOperationsLocale();

  const listenersRef = useRef(new Set<(ev: ReceptionEvent) => void>());
  const seenEventsRef = useRef<Map<string, number>>(new Map());
  const approvalToastSeenRef = useRef<Map<number, number>>(new Map());
  const backfillCursorRef = useRef<number>(-1);
  const { push: pushToast } = useToast();
  const { clearAuth, role } = useAuth();
  const employeeLoginPath = resolveEmployeeLoginPathFromUser();
  const queryClient = useQueryClient();
  const roleLower = String(role || '').trim().toLowerCase();
  const canSeeApprovalToasts = roleLower === 'super_reception' || roleLower === 'general_manager';

  const dispatch = useCallback((ev: ReceptionEvent) => {
    for (const listener of Array.from(listenersRef.current)) {
      try { listener(ev); } catch { /* ignore */ }
    }
  }, []);

  const logoutRef = useRef(false);
  const forceLogoutHandledRef = useRef(false);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (logoutRef.current) return null;
    const token = await fetchReceptionWsToken({ autoLogout: false });
    return token || null;
  }, []);

  const invalidateForEvent = useCallback((eventType: string) => {
    if (!eventType) return;
    const targets = [
      ...getReceptionInvalidationsForEvent(eventType),
      ...getAccountantInvalidationsForEvent(eventType),
    ];
    for (const target of targets) {
      scheduleRealtimeInvalidation(queryClient, target.queryKey);
    }
  }, [queryClient]);

  const pushActionToast = useCallback((payload: ReceptionEvent, eventType: string) => {
    const origin = String(payload.origin ?? payload.source ?? '');
    if (origin === 'postgresql') return;

    const toast = payload.toast;
    if (toast && typeof toast.message === 'string' && toast.message.trim()) {
      pushToast?.(
        toast.message,
        (toast.type ?? 'info') as 'success' | 'info' | 'warning' | 'danger',
        typeof toast.durationMs === 'number' ? toast.durationMs : undefined,
      );
      return;
    }

    if (eventType === 'task_assigned') {
      const taskTitle = (payload as { title?: string }).title || 'New task';
      const priority = (payload as { priority?: string }).priority || 'medium';
      const priorityBadge = priority === 'urgent' ? '[URGENT]' : priority === 'high' ? '[HIGH]' : '';
      pushToast?.(`${priorityBadge} Task assigned: ${taskTitle}`.trim(), priority === 'urgent' || priority === 'high' ? 'warning' : 'info', 4500);
      return;
    }

    if (eventType === 'reception_log_new') {
      if (!canSeeApprovalToasts) return;
      const level = (payload as { level?: string }).level;
      const status = (payload as { status?: string }).status;
      const action = (payload as { action?: string }).action;
      const logIdRaw = (payload as { id?: unknown }).id;
      const logId = typeof logIdRaw === 'number' ? logIdRaw : Number(logIdRaw);
      if (level === 'danger' && status === 'pending') {
        let allowToast = true;
        if (Number.isFinite(logId) && logId > 0) {
          const now = Date.now();
          const last = approvalToastSeenRef.current.get(logId);
          if (last && (now - last) < APPROVAL_TOAST_DEDUPE_MS) {
            allowToast = false;
          } else {
            approvalToastSeenRef.current.set(logId, now);
          }
        }
        if (allowToast) {
          const actionDisplay = action
            ? action.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
            : 'Action';
          pushToast?.(operationsFormat("Approval required: {0}", [actionDisplay]), 'warning', 6000);
        }
      }
    }
  }, [canSeeApprovalToasts, pushToast, operationsFormat]);

  const dispatchLegacyDomEvents = useCallback((payload: ReceptionEvent, eventType: string) => {
    if (eventType === 'task_status_update' && (payload as { task?: unknown }).task) {
      try { window.dispatchEvent(new CustomEvent('task_status_update', { detail: (payload as { task: unknown }).task })); } catch { /* ignore */ }
    }
    if (eventType === 'task_status_snapshot' && (payload as { tasks?: unknown }).tasks) {
      try { window.dispatchEvent(new CustomEvent('task_status_snapshot', { detail: (payload as { tasks: unknown }).tasks })); } catch { /* ignore */ }
    }

    const table = typeof payload.table === 'string' ? payload.table : '';
    const action = typeof payload.action === 'string' ? payload.action : '';
    const entityId = payload.entity_id ?? payload.record_id ?? payload.id ?? null;
    if (table && action) {
      try {
        window.dispatchEvent(new CustomEvent('database_change', { detail: { table, event: action, id: entityId, data: payload } }));
      } catch { /* ignore */ }
    }

    try {
      if (eventType === 'invoice_created' || eventType === 'invoice_updated' || eventType === 'invoice_deleted' || eventType === 'invoice_voided') {
        window.dispatchEvent(new CustomEvent('invoice_change', { detail: { event: eventType, data: payload } }));
      } else if (eventType === 'course_interest_created' || eventType === 'course_interest_updated' || eventType === 'course_interest_deleted') {
        window.dispatchEvent(new CustomEvent('course_interest_change', { detail: { event: eventType, data: payload } }));
      } else if (eventType.startsWith('student_')) {
        window.dispatchEvent(new CustomEvent('student_change', { detail: { event: eventType, data: payload } }));
      } else if (eventType.startsWith('group_')) {
        window.dispatchEvent(new CustomEvent('group_change', { detail: { event: eventType, data: payload } }));
      } else if (eventType === 'refund_update' || eventType === 'refund_created' || eventType === 'refund_updated') {
        window.dispatchEvent(new CustomEvent('refund_change', { detail: { event: eventType, data: payload } }));
      } else if (eventType.startsWith('placement_payment_')) {
        window.dispatchEvent(new CustomEvent('placement_payment_change', { detail: { event: eventType, data: payload } }));
      } else if (eventType === 'reception_log_new' || eventType === 'reception_log_update') {
        window.dispatchEvent(new CustomEvent('reception_log_change', { detail: { event: eventType, data: payload } }));
      }
    } catch {
      // Ignore CustomEvent failures.
    }
  }, []);

  const handleIncomingPayload = useCallback((parsed: unknown) => {
    const payload = normalizeIncomingEvent(parsed);
    if (!payload) return;

    const eventType = deriveEventType(payload);
    const eventId = deriveEventId(payload, eventType);
    if (eventId) {
      const now = Date.now();
      pruneDedupeMap(seenEventsRef.current, now);
      const seenAt = seenEventsRef.current.get(eventId);
      if (seenAt && (now - seenAt) < EVENT_DEDUPE_TTL_MS) {
        return;
      }
      seenEventsRef.current.set(eventId, now);
    }

    if (eventType === 'force_logout') {
      if (!forceLogoutHandledRef.current) {
        forceLogoutHandledRef.current = true;
        logoutRef.current = true;
        try {
          dispatchLogoutAfterAuthClear(clearAuth, { reason: 'force_logout' });
        } catch { /* ignore */ }
        pushToast?.(operationsText("You have been logged out"), 'warning');
        setTimeout(() => {
          if (window.location.pathname !== employeeLoginPath) {
            window.location.replace(employeeLoginPath);
          }
        }, 600);
      }
      return;
    }

    if (eventType) {
      invalidateForEvent(eventType);
    }
    pushActionToast(payload, eventType);
    dispatchLegacyDomEvents(payload, eventType);
    dispatch(payload);
  }, [clearAuth, dispatch, dispatchLegacyDomEvents, employeeLoginPath, invalidateForEvent, pushActionToast, pushToast, operationsText]);

  const handleMessage = useCallback((_ev: MessageEvent, parsed: unknown) => {
    handleIncomingPayload(parsed);
  }, [handleIncomingPayload]);

  const { connectionState, reconnect: wsReconnect } = useWebSocket('/reception/ws', {
    enabled: enabled && !logoutRef.current,
    maxAttempts: MAX_RETRY,
    getToken,
    onMessage: handleMessage,
  });

  useEffect(() => {
    if (!enabled || logoutRef.current || connectionState === 'open' || connectionState === 'closed') {
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let intervalId: number | null = null;

    const pollBackfill = async () => {
      if (cancelled || inFlight || logoutRef.current) return;
      inFlight = true;
      const controller = new AbortController();
      try {
        const result = await fetchReceptionEventsSince(backfillCursorRef.current, {
          autoLogout: false,
          signal: controller.signal,
        });
        if (cancelled || !result) return;

        const events = result.events;
        for (const event of events) {
          const idx = Number(event.idx);
          if (Number.isFinite(idx)) {
            backfillCursorRef.current = Math.max(backfillCursorRef.current, idx);
          }
          handleIncomingPayload(event);
        }
        backfillCursorRef.current = Math.max(backfillCursorRef.current, result.last);
      } catch {
        // Keep the websocket reconnect loop authoritative; polling is only a fallback.
      } finally {
        inFlight = false;
      }
    };

    void pollBackfill();
    intervalId = window.setInterval(() => {
      void pollBackfill();
    }, BACKFILL_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [connectionState, enabled, handleIncomingPayload]);

  useEffect(() => {
    const onLogout = () => {
      logoutRef.current = true;
      clearRealtimeInvalidations(queryClient);
    };
    window.addEventListener('app:logout', onLogout);
    return () => {
      window.removeEventListener('app:logout', onLogout);
      clearRealtimeInvalidations(queryClient);
    };
  }, [queryClient]);

  const subscribe = useCallback((cb: (ev: ReceptionEvent) => void) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const reconnect = useCallback(() => {
    if (logoutRef.current) return;
    if (connectionState !== 'open') {
      pushToast?.(operationsText("Reconnecting…"), 'info');
    }
    wsReconnect();
  }, [connectionState, wsReconnect, pushToast, operationsText]);

  const value: RealtimeContextShape = useMemo(() => ({
    subscribe,
    connectionState: enabled ? connectionState : 'closed',
    reconnect: enabled ? reconnect : () => { /* no-op */ },
  }), [connectionState, enabled, reconnect, subscribe]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export default RealtimeProvider;
